import React from 'react';

import { CardHeader, CardTitle } from '../ui/card';
import {
  FileText,
  AlertCircle,
  Shield,
  Mail,
  Pill,
  UserCheck,
  Sparkles,
  RefreshCw,
  MessageSquare
} from 'lucide-react';
import { ComposeMessageDialog } from '../messaging/ComposeMessageDialog';
import { NewBadge } from '../NewBadge';
import { AIReferralButton } from '../referrals/AIReferralDialog';
import { AskReviewButton } from '../reviews/AskReviewButton';

export const RecordsHeader = ({ activeTab, complianceOnly, dashboardData, loadDashboard, loadHipaaConsent, loadSmileTransformations, messagePatientOpen, patientFollowUps, realPatients, refreshing, selectedPatientId, setActiveTab, setMessagePatientOpen, setRefreshing, toast }) => (
  <>
    <CardHeader className="pb-2">
      {/* Row 1: Title + Action buttons */}
      <div className="flex items-center justify-between mb-3">
        <CardTitle className="flex items-center text-2xl">
            {activeTab === 'procedures' && <FileText className="h-6 w-6 mr-2 text-green-600" />}
            {activeTab === 'consents' && <Shield className="h-6 w-6 mr-2 text-indigo-600" />}
            {activeTab === 'compliance' && <Shield className="h-6 w-6 mr-2 text-amber-600" />}
            {activeTab === 'medcards' && <Pill className="h-6 w-6 mr-2 text-orange-600" />}
            {activeTab === 'referrals' && <UserCheck className="h-6 w-6 mr-2 text-teal-600" />}
            {activeTab === 'problems' && <AlertCircle className="h-6 w-6 mr-2 text-red-600" />}
            {activeTab === 'smiles' && <Sparkles className="h-6 w-6 mr-2 text-purple-600" />}
            {activeTab === 'hipaa' && <Shield className="h-6 w-6 mr-2 text-rose-600" />}
            {activeTab === 'messages' && <MessageSquare className="h-6 w-6 mr-2 text-sky-600" />}
            {activeTab === 'activity' && <FileText className="h-6 w-6 mr-2 text-slate-600" />}
            Patient Records
        </CardTitle>
        {selectedPatientId && (
          <div className="flex items-center gap-2">
            <button
              data-testid="refresh-records-button"
              onClick={async () => {
                setRefreshing(true);
                await loadDashboard(true);
                setTimeout(() => setRefreshing(false), 600);
                toast({ title: "Refreshed", description: "Patient records updated." });
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
              title="Refresh patient records to see latest updates"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              data-testid="message-patient-button"
              onClick={() => setMessagePatientOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Message Patient <NewBadge testId="message-patient-new-badge" />
            </button>
            <AIReferralButton patientId={selectedPatientId} className="h-auto px-3 py-1.5 text-sm text-sky-700 border-sky-200" />
            <AskReviewButton patientId={selectedPatientId} toast={toast} />
            <ComposeMessageDialog
              open={messagePatientOpen}
              onOpenChange={setMessagePatientOpen}
              patient={realPatients.find((p) => p.id === selectedPatientId) || (dashboardData?.recentPatients || []).find((p) => p.id === selectedPatientId) || null}
            />
            <button
              data-testid="send-portal-link-button"
            onClick={async () => {
              try {
                const token = localStorage.getItem('dentalToken');
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/send-link`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ patientId: selectedPatientId })
                });
                const data = await res.json();
                if (data.success) {
                  toast({ title: "Portal Link Sent", description: "Patient portal link sent via email and/or SMS." });
                } else {
                  toast({ title: "Error", description: data.detail || "Could not send portal link. Check patient email/phone.", variant: "destructive" });
                }
              } catch (err) {
                toast({ title: "Error", description: "Failed to send portal link.", variant: "destructive" });
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
          >
            <Mail className="h-3.5 w-3.5" />
            Send Portal Link
          </button>
          </div>
        )}
      </div>
      {/* Row 2: Tab navigation */}
          <div className="flex flex-wrap items-center bg-gray-100 rounded-lg p-1" style={{ gap: '2px' }}>
            <button
              onClick={() => { 
                console.log('📑 Switching to Procedures tab, Patient:', selectedPatientId); 
                setActiveTab('procedures'); 
              }}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'procedures'
                  ? 'bg-white text-green-700 font-medium shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Procedures
            </button>
            <button
              onClick={() => { 
                console.log('📑 Switching to Consents tab, Patient:', selectedPatientId); 
                setActiveTab('consents'); 
              }}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'consents'
                  ? 'bg-white text-indigo-700 font-medium shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Informed Consents
            </button>
            <button
              onClick={() => {
                console.log('📑 Switching to Compliance Forms tab, Patient:', selectedPatientId);
                setActiveTab('compliance');
              }}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'compliance'
                  ? 'bg-white text-amber-700 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="patient-records-compliance-tab"
            >
              Compliance Forms
              {complianceOnly.length > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                  activeTab === 'compliance' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'
                }`}>
                  {complianceOnly.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('followups')}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'followups'
                  ? 'bg-white text-emerald-700 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="patient-records-followups-tab"
            >
              Follow-Ups
              {patientFollowUps.length > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                  activeTab === 'followups' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'
                }`}>
                  {patientFollowUps.length}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                console.log('📑 Switching to Med Cards tab, Patient:', selectedPatientId);
                setActiveTab('medcards');
              }}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'medcards'
                  ? 'bg-white text-orange-700 font-medium shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Med Cards
            </button>
            <button
              onClick={() => {
                console.log('📑 Switching to Referrals tab, Patient:', selectedPatientId);
                setActiveTab('referrals');
              }}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'referrals'
                  ? 'bg-white text-teal-700 font-medium shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Referrals
            </button>
            <button
              onClick={() => {
                console.log('📑 Switching to Problems & Emergencies tab, Patient:', selectedPatientId);
                setActiveTab('problems');
              }}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'problems'
                  ? 'bg-white text-red-700 font-medium shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Problems
            </button>
            <button
              onClick={() => {
                console.log('😁 Switching to Smile Transformations tab, Patient:', selectedPatientId);
                setActiveTab('smiles');
                if (selectedPatientId) loadSmileTransformations(selectedPatientId);
              }}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'smiles'
                  ? 'bg-white text-purple-700 font-medium shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Smiles
            </button>
            <button
              onClick={() => setActiveTab('ecards')}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'ecards'
                  ? 'bg-white text-pink-700 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="patient-records-ecards-tab"
            >
              eCards
            </button>
            <button
              onClick={() => {
                setActiveTab('hipaa');
                if (selectedPatientId) loadHipaaConsent(selectedPatientId);
              }}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'hipaa'
                  ? 'bg-white text-rose-700 font-medium shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              HIPAA
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'messages'
                  ? 'bg-white text-sky-700 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="patient-records-messages-tab"
            >
              Messages <NewBadge className="ml-1" testId="patient-records-messages-tab-new-badge" />
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                activeTab === 'activity'
                  ? 'bg-white text-slate-800 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="patient-records-activity-tab"
            >
              Notes <NewBadge className="ml-1" testId="patient-records-activity-tab-new-badge" />
            </button>
          </div>
    </CardHeader>
  </>
);
